import { isValidWeeklySlug } from './weeklyTab.js';

/**
 * Configuration schema that defines the expected structure and validation rules
 * This can be easily modified to support new tab types or change validation rules
 */
const CONFIG_SCHEMA = {
  type: 'object',
  required: ['tabs'],
  properties: {
    tabs: {
      type: 'array',
      minItems: 1,
      items: {
        type: 'object',
        required: ['name', 'type', 'properties', 'slug'],
        properties: {
          name: {
            type: 'string',
            minLength: 1
          },
          slug: {
            type: 'string',
            minLength: 1
          },
          type: {
            type: 'string',
            enum: ['gallery', 'gi-ai', 'weekly', 'embed', 'external']
          },
          admin_only: {
            type: 'boolean'
          },
          properties: {
            type: 'object'
          }
        },
        // Rules specific to the tab type. They belong on the tab schema: the tab is the only
        // value that carries `type` (nested under "properties", they never ran).
        typeValidators: {
          'gallery': {
            required: ['projects'],
            properties: {
              projects: {
                type: 'array',
                items: {
                  type: 'integer'
                }
              }
            }
          },
          'gi-ai': {
            required: ['clientId'],
            properties: {
              clientId: {
                type: 'integer'
              }
            }
          },
          'weekly': {
            required: ['clientSlug'],
            properties: {
              clientSlug: {
                type: 'string',
                minLength: 1,
                format: 'weekly-slug'
              }
            }
          },
          'embed': {
            required: ['src'],
            properties: {
              src: {
                type: 'string',
                format: 'https-url'
              }
            }
          },
          'external': {
            required: ['src'],
            properties: {
              src: {
                type: 'string',
                format: 'url'
              }
            }
          }
        }
      }
    }
  }
};

/**
 * Fonction utilitaire pour créer un slug à partir d'un texte
 * @param {string} text - Le texte à transformer en slug
 * @returns {string} - Le slug généré
 */
function createSlug(text) {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .normalize('NFD') // Décomposer les caractères accentués
    .replace(/[\u0300-\u036f]/g, '') // Supprimer les accents
    .replace(/\s+/g, '-') // Remplacer les espaces par des tirets
    .replace(/[^\w-]+/g, '') // Supprimer les caractères non alphanumériques
    .replace(/--+/g, '-') // Remplacer les multiples tirets par un seul
    .replace(/^-+/, '') // Supprimer les tirets au début
    .replace(/-+$/, ''); // Supprimer les tirets à la fin
}

/**
 * Validates a value against a type
 * @param {any} value - The value to validate
 * @param {string} type - The expected type
 * @returns {boolean} - Whether the value matches the type
 */
function validateType(value, type) {
  switch (type) {
    case 'string':
      return typeof value === 'string';
    case 'object':
      return typeof value === 'object' && value !== null && !Array.isArray(value);
    case 'array':
      return Array.isArray(value);
    case 'integer':
      return Number.isInteger(value);
    default:
      return true;
  }
}

/**
 * Validates a value against a format
 * @param {string} value - The value to validate
 * @param {string} format - The expected format
 * @returns {boolean} - Whether the value matches the format
 */
function validateFormat(value, format) {
  try {
    if (format === 'url') {
      new URL(value);
      return true;
    } else if (format === 'https-url') {
      const url = new URL(value);
      return url.protocol === 'https:';
    } else if (format === 'weekly-slug') {
      return isValidWeeklySlug(value);
    }
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Validates a value against a schema
 * @param {any} value - The value to validate
 * @param {Object} schema - The schema to validate against
 * @param {string} path - The current path in the object (for error messages)
 * @returns {Object} - { valid: boolean, error: string }
 */
function validateAgainstSchema(value, schema, path = '') {
  // Check type
  if (schema.type && !validateType(value, schema.type)) {
    return {
      valid: false,
      error: `${path} must be ${/^[aeiou]/.test(schema.type) ? 'an' : 'a'} ${schema.type}`
    };
  }

  // Check enum
  if (schema.enum && !schema.enum.includes(value)) {
    return {
      valid: false,
      error: `${path} must be one of: ${schema.enum.join(', ')}`
    };
  }

  // Check minLength for strings
  if (schema.type === 'string' && schema.minLength && value.trim().length < schema.minLength) {
    return {
      valid: false,
      error: `${path} must not be empty`
    };
  }

  // Check format for strings
  if (schema.type === 'string' && schema.format && !validateFormat(value, schema.format)) {
    if (schema.format === 'https-url') {
      return {
        valid: false,
        error: `${path} must be a valid HTTPS URL`
      };
    } else if (schema.format === 'weekly-slug') {
      return {
        valid: false,
        error: `${path} must be a Weekly slug (lowercase letters, digits and dashes)`
      };
    } else {
      return {
        valid: false,
        error: `${path} must be a valid URL`
      };
    }
  }

  // Check minItems for arrays
  if (schema.type === 'array' && schema.minItems && value.length < schema.minItems) {
    return {
      valid: false,
      error: `${path} must have at least ${schema.minItems} item(s)`
    };
  }

  // Check array items
  if (schema.type === 'array' && schema.items) {
    for (let i = 0; i < value.length; i++) {
      const itemResult = validateAgainstSchema(value[i], schema.items, `${path}[${i}]`);
      if (!itemResult.valid) {
        return itemResult;
      }
    }
  }

  // Check required properties
  if (schema.type === 'object' && schema.required) {
    for (const requiredProp of schema.required) {
      // Cas spécial pour le slug: si manquant mais nom présent, générer automatiquement
      if (requiredProp === 'slug' && value[requiredProp] === undefined && value.name) {
        value.slug = createSlug(value.name);
        continue;
      }

      if (value[requiredProp] === undefined) {
        if (requiredProp === 'slug') {
          return {
            valid: false,
            error: `${path} doit avoir une propriété "slug". Un slug sera automatiquement généré à partir du nom de l'onglet.`
          };
        } else {
          return {
            valid: false,
            error: `${path} doit avoir une propriété "${requiredProp}"`
          };
        }
      }
    }
  }

  // Check object properties
  if (schema.type === 'object' && schema.properties) {
    for (const propName in schema.properties) {
      if (value[propName] !== undefined) {
        const propResult = validateAgainstSchema(
          value[propName],
          schema.properties[propName],
          path ? `${path}.${propName}` : propName
        );
        if (!propResult.valid) {
          return propResult;
        }
      }
    }
  }

  // Check type-specific validators for properties
  if (schema.type === 'object' && schema.typeValidators && value.type) {
    const typeValidator = schema.typeValidators[value.type];
    if (typeValidator) {
      // Check required properties for this type
      if (typeValidator.required) {
        for (const requiredProp of typeValidator.required) {
          if (!value.properties || value.properties[requiredProp] === undefined) {
            return {
              valid: false,
              error: `${path} of type "${value.type}" must have a "${requiredProp}" property`
            };
          }
        }
      }

      // Check properties for this type
      if (typeValidator.properties && value.properties) {
        for (const propName in typeValidator.properties) {
          if (value.properties[propName] !== undefined) {
            const propResult = validateAgainstSchema(
              value.properties[propName],
              typeValidator.properties[propName],
              path ? `${path}.properties.${propName}` : `properties.${propName}`
            );
            if (!propResult.valid) {
              return propResult;
            }
          }
        }
      }
    }
  }

  return { valid: true, error: '' };
}

/**
 * Validates the structure and content of a cockpit configuration
 * @param {Object} config - The cockpit configuration to validate
 * @returns {Object} - { valid: boolean, error: string }
 */
export function validateCockpitConfig(config) {
  return validateAgainstSchema(config, CONFIG_SCHEMA);
}

/**
 * Validates a single tab without modifying it (a missing slug is generated on a copy)
 * @param {Object} tab - The tab to validate
 * @param {number} index - Its position in the config, for the error path
 * @returns {Object} - { valid: boolean, error: string }
 */
export function validateTab(tab, index = 0) {
  const { valid, error } = validateCockpitConfig({ tabs: [JSON.parse(JSON.stringify(tab ?? null))] });
  return { valid, error: error.replace(/^tabs\[0\]/, `tabs[${index}]`) };
}

/**
 * Names the tab an error is about: "tabs[3].properties.src …" becomes "Onglet « Ventes » : properties.src …"
 * @param {string} error - An error of validateCockpitConfig, or of the backend (same format)
 * @param {Array} tabs - The tabs of the validated config
 * @returns {string} - The error, with the tab's name when it has one
 */
export function describeConfigError(error, tabs = []) {
  const match = /^tabs\[(\d+)\](\.)?/.exec(error || '');
  if (!match) return error;

  const index = Number(match[1]);
  const name = typeof tabs?.[index]?.name === 'string' ? tabs[index].name.trim() : '';
  const label = name ? `Onglet « ${name} »` : `Onglet n°${index + 1}`;
  return `${label}${match[2] ? ' : ' : ''}${error.slice(match[0].length)}`;
}